import { produce } from 'immer'

import { OfferStatus } from '@wwt/entities/api/search-response'
import { Offer } from '@wwt/entities/search-response'

import { assert } from '@/shared/lib/assert'

import { LikeTypes } from '../config/likeQueryKeys'

/*
 * Add offer to likes list and AUTO change status to AVAILABLE
 */
const addOfferWithChangeStatusToLikesList = (
	saved: LikeTypes['all'] | undefined,
	offer: Offer
) => {
	assert(
		saved !== undefined,
		'Like mutation utils add offer saved likes is undefined'
	)

	const offerId = offer.IDs.nonPersistingOfferId

	const isOfferSaved =
		saved.find(offer => offer.IDs.nonPersistingOfferId === offerId) !==
		undefined

	if (isOfferSaved) {
		return saved.map(offer => {
			if (offer.IDs.nonPersistingOfferId !== offerId) return offer

			return {
				...offer,
				IDs: {
					...offer.IDs,
					status: OfferStatus.AVAILABLE
				}
			}
		})
	}

	return [
		...saved,
		{
			...offer,
			IDs: {
				...offer.IDs,
				status: OfferStatus.AVAILABLE
			}
		}
	]
}

const replaceOfferInLikedList = (
	saved: LikeTypes['all'] | undefined,
	offerId: Offer['IDs']['nonPersistingOfferId'],
	newOffer: Offer
) => {
	assert(
		saved !== undefined,
		'Like mutation utils replace offer saved likes is undefined'
	)

	const isOfferSaved =
		saved.find(offer => offer.IDs.nonPersistingOfferId === offerId) !==
		undefined

	if (!isOfferSaved) {
		throw new Error(`Old offer with id ${offerId} is not saved`)
	}

	return saved.map(offer => {
		if (offer.IDs.nonPersistingOfferId !== offerId) return offer

		return newOffer
	})
}

const removeOfferFromLikesList = (
	saved: LikeTypes['all'] | undefined,
	offer: Offer
) => {
	assert(saved !== undefined, 'Like mutation utils saved likes is undefined')

	return saved.filter(
		savedOffer =>
			savedOffer.IDs.nonPersistingOfferId !== offer.IDs.nonPersistingOfferId
	)
}

const addOfferToLikeIds = (
	saved: LikeTypes['ids'] | undefined,
	offer: Offer,
	setId: string | undefined
) => {
	assert(saved !== undefined, 'Like mutation utils saved like ids is undefined')
	assert(setId !== undefined, 'Like mutation utils setId like ids is undefined')

	const offerUniqueId = offer.IDs.nonPersistingOfferId

	return produce(saved, saved => {
		saved[offerUniqueId] = setId
	})
}

const removeOfferFromLikeIds = (
	saved: LikeTypes['ids'] | undefined,
	offer: Offer
) => {
	assert(
		saved !== undefined,
		'Like mutation utils saved like ids is undefined on remove'
	)

	const offerUniqueId = offer.IDs.nonPersistingOfferId

	return produce(saved, saved => {
		delete saved[offerUniqueId]
	})
}

export const LikeMutationUtils = {
	addOfferWithChangeStatusToLikesList,
	replaceOfferInLikedList,
	removeOfferFromLikesList,

	addOfferToLikeIds,
	removeOfferFromLikeIds
}
